import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Select, { type StylesConfig } from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faCheck,
  faTimes,
  faCopy,
  faTrash,
  faPlug,
  faFileArrowDown,
  faImage,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

import { useTheme } from "../../../context/ThemeContext";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { getCustomSelectStyles } from "../../admin/shared";
import { useEvidenceSubscription } from "../../../hooks/useEvidenceSubscription";
import { useEvidenceSocketStore } from "../../../store/admin/evidenceSocketStore";
import { useCompanyIntegratorsStore } from "../../../store/admin/companyIntegratorsStore";
import { useCompanyIntegrators } from "../../../features/integradores/queries";
import {
  useFilteredEvidences,
  useEvidenceAdapters,
  useEvidenceRetransmissions,
  useEvidenceStats,
} from "../../../store/admin/evidenceSocketSelector";
import type { EvidenceFilter } from "../../../evidenceSocket";
import type { EvidenceItem } from "../../../store/admin/evidenceSocketStore";
import { useFleetCompanyScope } from "../hooks/useFleetCompanyScope";
import {
  useIntegradoresOptions,
  type SelectOption,
} from "../hooks/useIntegradoresOptions";
import {
  formatPayload,
  formatTime,
  payloadFileMeta,
  downloadText,
  screenshotPayload,
  buildFileName,
} from "../helpers";
import type { EvidenceScopeMode, IntegradoresMode } from "../types";

function useStyleTokens(isDarkMode: boolean) {
  const btnGhost = isDarkMode
    ? "bg-transparent border border-dark-border rounded text-xs px-2.5 py-1 text-gray-300 cursor-pointer hover:bg-dark-card transition-colors flex items-center gap-1"
    : "bg-transparent border border-gray-300 rounded text-xs px-2.5 py-1 text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors flex items-center gap-1";

  const inpCls = isDarkMode
    ? "bg-dark-card border border-dark-border rounded text-slate-100 text-sm px-2.5 py-1.5 outline-none focus:border-primary-500"
    : "bg-white border border-gray-300 rounded text-gray-800 text-sm px-2.5 py-1.5 outline-none focus:border-primary-500";

  return { btnGhost, inpCls };
}

const ALL_VALUE = "__all__";

interface Props {
  mode: IntegradoresMode;
}

export default function IntegradoresMonitor({ mode }: Props) {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation("common");
  const { btnGhost, inpCls } = useStyleTokens(isDarkMode);
  const selectStyles = useMemo(
    () => getCustomSelectStyles(isDarkMode) as StylesConfig<SelectOption, false>,
    [isDarkMode]
  );

  const [scopeMode, setScopeMode] = useState<EvidenceScopeMode>("all");
  const [scopeOption, setScopeOption] = useState<SelectOption | null>(null);
  const [deviceOption, setDeviceOption] = useState<SelectOption | null>(null);
  const [retransmissionOption, setRetransmissionOption] =
    useState<SelectOption | null>(null);
  const [selected, setSelected] = useState<EvidenceItem | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [busyShot, setBusyShot] = useState<string | null>(null);

  const fleetCompany = useFleetCompanyScope();
  const companyId = useCompaniesStore((state) => state.companyId);
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const companies = useCompaniesStore((state) => state.companies);
  const fetchAllCompanies = useCompaniesStore((state) => state.fetchAllCompanies);
  const fetchCompany = useCompaniesStore((state) => state.fetchCompany);
  const [resolvedNames, setResolvedNames] = useState<Record<string, string>>({});

  useEffect(() => {
    if (mode === "internal") fetchAllCompanies();
  }, [mode, fetchAllCompanies]);

  // Si la compañía activa no está en las listas cargadas, la buscamos por id
  // para mostrar su nombre (y no el id) en el badge.
  useEffect(() => {
    if (!companyId) return;
    const list = allCompanies.length ? allCompanies : companies;
    if (list.some((c) => c.id === companyId)) return;
    if (resolvedNames[companyId]) return;
    let cancelled = false;
    fetchCompany(companyId)
      .then((res: unknown) => {
        const data =
          (res as { data?: { name?: string; business_name?: string } })?.data ??
          (res as { name?: string; business_name?: string });
        const name = data?.name || data?.business_name;
        if (!cancelled && name) {
          setResolvedNames((prev) => ({ ...prev, [companyId]: name }));
        }
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, [companyId, allCompanies, companies, fetchCompany, resolvedNames]);

  const activeCompanyName = useMemo(() => {
    if (!companyId) return null;
    const list = allCompanies.length ? allCompanies : companies;
    const c = list.find((co) => co.id === companyId);
    return c?.name || c?.business_name || resolvedNames[companyId] || companyId;
  }, [companyId, allCompanies, companies, resolvedNames]);

  const companyNameById = useMemo(() => {
    const list = allCompanies.length ? allCompanies : companies;
    const map = new Map(
      list.map((c) => [c.id, c.name || c.business_name || c.id])
    );
    return (id: string) => map.get(id) || resolvedNames[id] || id;
  }, [allCompanies, companies, resolvedNames]);
  const {
    companyOptions,
    deviceOptions,
    integratorOptions,
    devicesLoading,
    loadCompanies,
    loadDevices,
    loadIntegrators,
  } = useIntegradoresOptions();

  // Cobertura por retransmisión: solo los devices retransmitidos ofrecen XML.
  const hasRetransmissions = useCompanyIntegratorsStore(
    (state) => state.hasRetransmissions
  );
  const coversAllDevices = useCompanyIntegratorsStore(
    (state) => state.coversAllDevices
  );
  const coveredDeviceIds = useCompanyIntegratorsStore(
    (state) => state.coveredDeviceIds
  );
  const companyRetransmissions = useCompanyIntegratorsStore(
    (state) => state.retransmissions
  );

  const coverageCompanyId = mode === "fleet" ? fleetCompany : companyId;
  // Carga vía TanStack con write-through al store (no toca el evidenceSocket).
  useCompanyIntegrators(coverageCompanyId || null);

  const coveredDeviceOptions = useMemo<SelectOption[]>(
    () =>
      coversAllDevices
        ? deviceOptions
        : deviceOptions.filter((o) => coveredDeviceIds.has(o.value)),
    [deviceOptions, coversAllDevices, coveredDeviceIds]
  );

  const deviceNoOptionsMessage = () =>
    hasRetransmissions
      ? t("integradores.noCoveredDevices")
      : t("integradores.noRetransmissions");

  useEffect(() => {
    if (mode === "fleet") {
      loadDevices();
      return;
    }
    if (scopeMode === "company") loadCompanies();
    else if (scopeMode === "device") loadDevices();
    else if (scopeMode === "integrator") loadIntegrators();
  }, [mode, scopeMode, loadCompanies, loadDevices, loadIntegrators]);

  // Al cambiar de compañía, recargar los devices de esa compañía (con loader)
  // y limpiar la unidad seleccionada para no quedar con una de otra compañía.
  useEffect(() => {
    if (mode === "fleet") {
      setDeviceOption(null);
      setRetransmissionOption(null);
      loadDevices();
    } else if (scopeMode === "device") {
      setScopeOption(null);
      loadDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Al elegir "Por compañía", precargar la compañía ya seleccionada
  useEffect(() => {
    if (mode !== "internal" || scopeMode !== "company" || scopeOption || !companyId) {
      return;
    }
    const opt = companyOptions.find((o) => o.value === companyId);
    if (opt) setScopeOption(opt);
  }, [mode, scopeMode, scopeOption, companyId, companyOptions]);

  const companyScopeOptions = useMemo<SelectOption[]>(
    () => [
      { value: ALL_VALUE, label: t("integradores.scope.all") },
      ...companyOptions,
    ],
    [companyOptions, t]
  );

  const retransmissionScopeOptions = useMemo<SelectOption[]>(
    () =>
      companyRetransmissions.map((r) => ({
        value: r.id,
        label: r.customer_name ? `${r.label} · ${r.customer_name}` : r.label,
      })),
    [companyRetransmissions]
  );

  const retransmissionNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of companyRetransmissions) {
      map.set(r.id, r.customer_name || r.label || r.id);
    }
    return map;
  }, [companyRetransmissions]);

  const scopeModeOptions = useMemo<SelectOption[]>(
    () => [
      { value: "all", label: t("integradores.scope.all") },
      { value: "company", label: t("integradores.scope.company") },
      { value: "device", label: t("integradores.scope.device") },
      { value: "integrator", label: t("integradores.scope.integrator") },
      { value: "retransmission", label: t("integradores.scope.retransmission") },
    ],
    [t]
  );

  const filter = useMemo<EvidenceFilter>(() => {
    if (mode === "fleet") {
      if (!fleetCompany) return {};
      if (deviceOption) return { device_ids: [deviceOption.value] };
      if (retransmissionOption)
        return { retransmission_ids: [retransmissionOption.value] };
      return { company_ids: [fleetCompany] };
    }

    if (scopeMode === "all") return { all: true };
    if (scopeMode === "company") {
      if (scopeOption?.value === ALL_VALUE) return { all: true };
      if (!scopeOption) return {};
      return { company_ids: [scopeOption.value] };
    }
    if (!scopeOption) return {};
    if (scopeMode === "device") return { device_ids: [scopeOption.value] };
    if (scopeMode === "retransmission")
      return { retransmission_ids: [scopeOption.value] };
    return { integrators: [scopeOption.value] };
  }, [
    mode,
    fleetCompany,
    deviceOption,
    retransmissionOption,
    scopeMode,
    scopeOption,
  ]);

  const { isConnected, joinedRooms } = useEvidenceSubscription(filter);
  const connecting = useEvidenceSocketStore((state) => state.connecting);

  const clearEvidences = useEvidenceSocketStore((state) => state.clearEvidences);
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);
  const prevFilterKey = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      clearEvidences();
      setSelected(null);
    }
  }, [filterKey, clearEvidences]);

  const evidences = useFilteredEvidences();
  const adapters = useEvidenceAdapters();
  const retransmissionsInEvidence = useEvidenceRetransmissions();
  const stats = useEvidenceStats();

  const statusFilter = useEvidenceSocketStore((state) => state.statusFilter);
  const setStatusFilter = useEvidenceSocketStore(
    (state) => state.setStatusFilter
  );
  const selectedAdapter = useEvidenceSocketStore(
    (state) => state.selectedAdapter
  );
  const setSelectedAdapter = useEvidenceSocketStore(
    (state) => state.setSelectedAdapter
  );
  const selectedRetransmission = useEvidenceSocketStore(
    (state) => state.selectedRetransmission
  );
  const setSelectedRetransmission = useEvidenceSocketStore(
    (state) => state.setSelectedRetransmission
  );
  const searchQuery = useEvidenceSocketStore((state) => state.searchQuery);
  const setSearchQuery = useEvidenceSocketStore((state) => state.setSearchQuery);

  const adapterFilterOptions = useMemo<SelectOption[]>(
    () => adapters.map((a) => ({ value: a, label: a })),
    [adapters]
  );

  const retransmissionFilterOptions = useMemo<SelectOption[]>(
    () =>
      retransmissionsInEvidence.map((r) => ({
        value: r.id,
        label: retransmissionNameById.get(r.id) || r.label,
      })),
    [retransmissionsInEvidence, retransmissionNameById]
  );

  const showRetransmissionFilter =
    mode === "internal" &&
    scopeMode !== "retransmission" &&
    retransmissionFilterOptions.length > 0;

  const copyText = async (text: string, which: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied(null);
    }
  };

  const handleDownloadText = (field: "request" | "response") => {
    if (!selected) return;
    const { ext, mime } = payloadFileMeta(selected[field]);
    downloadText(
      formatPayload(selected[field]),
      buildFileName(selected, field, ext),
      mime
    );
  };

  const handleScreenshot = async (field: "request" | "response") => {
    if (!selected) return;
    const key = `${field}-png`;
    setBusyShot(key);
    try {
      await screenshotPayload(
        `${selected.imei || selected.device_id} - ${field.toUpperCase()}`,
        formatTime(selected.sent_at),
        formatPayload(selected[field]),
        buildFileName(selected, field, "png")
      );
    } catch {
      /* noop */
    } finally {
      setBusyShot(null);
    }
  };

  const noFleetCompany = mode === "fleet" && !fleetCompany;

  return (
    <div
      className={`flex flex-col h-full overflow-hidden ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"
        }`}
    >
      {scopeMode === "device" && activeCompanyName && (
        <div
          className={`ml-4 mt-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border w-fit ${isDarkMode
              ? "bg-primary-500/10 border-primary-500/30 text-primary-400"
              : "bg-primary-50 border-primary-200 text-primary-600"
            }`}
          title={`${t("integradores.activeCompany")}: ${activeCompanyName}`}
        >
          <i className="ri-building-2-line text-[12px]"></i>
          <span className="opacity-70">
            {t("integradores.activeCompany")}:
          </span>
          <span className="font-semibold truncate max-w-[160px]">
            {activeCompanyName}
          </span>
        </div>
      )}
      <div
        className={`px-3 sm:px-4 py-2.5 flex flex-wrap items-center gap-2 shrink-0 border-b shadow-sm ${isDarkMode ? "bg-dark-bg border-dark-border" : "bg-white border-gray-200"
          }`}
      >
        <div
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${connecting && !isConnected
              ? "bg-amber-500/15 text-amber-400 border border-amber-500/30"
              : isConnected
                ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                : "bg-red-500/15 text-red-400 border border-red-500/30"
            }`}
        >
          <FontAwesomeIcon
            icon={connecting && !isConnected ? faSpinner : faPlug}
            className={`text-[10px] ${connecting && !isConnected ? "animate-spin" : ""
              }`}
          />
          <span>
            {connecting && !isConnected
              ? t("integradores.connecting")
              : isConnected
                ? t("integradores.connected")
                : t("integradores.disconnected")}
          </span>
        </div>

        {mode === "internal" && (
          <div className="flex items-start gap-1.5 flex-wrap min-w-0 w-full sm:w-auto">
            <div className="w-full sm:w-44">
              <Select<SelectOption>
                value={
                  scopeModeOptions.find((o) => o.value === scopeMode) ?? null
                }
                onChange={(opt) => {
                  setScopeMode((opt?.value as EvidenceScopeMode) ?? "all");
                  setScopeOption(null);
                  setSelectedAdapter(null);
                  setSelectedRetransmission(null);
                }}
                options={scopeModeOptions}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isSearchable={false}
                placeholder={t("integradores.scopeLabel")}
              />
            </div>
            {scopeMode !== "all" && (
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <div className="w-full sm:w-64">
                  <Select<SelectOption>
                    value={scopeOption}
                    onChange={(opt) => setScopeOption(opt)}
                    options={
                      scopeMode === "company"
                        ? companyScopeOptions
                        : scopeMode === "device"
                          ? coveredDeviceOptions
                          : scopeMode === "retransmission"
                            ? retransmissionScopeOptions
                            : integratorOptions
                    }
                    placeholder={t(`integradores.select.${scopeMode}`)}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isClearable
                    isLoading={scopeMode === "device" && devicesLoading}
                    {...(scopeMode === "device"
                      ? { noOptionsMessage: deviceNoOptionsMessage }
                      : {})}
                  />
                </div>
                {scopeMode === "device" &&
                  coverageCompanyId &&
                  !hasRetransmissions && (
                    <p className="text-[11px] text-amber-500 flex items-center gap-1">
                      <i className="ri-information-line"></i>
                      {t("integradores.noRetransmissions")}
                    </p>
                  )}
              </div>
            )}
          </div>
        )}

        {mode === "fleet" && (
          <>
            <div className="flex flex-col gap-1 w-full sm:w-auto">
              <div className="w-full sm:w-64">
                <Select<SelectOption>
                  value={deviceOption}
                  onChange={(opt) => {
                    setDeviceOption(opt);
                    if (opt) setRetransmissionOption(null);
                  }}
                  options={coveredDeviceOptions}
                  placeholder={t("integradores.select.device")}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                  isDisabled={noFleetCompany}
                  isLoading={devicesLoading}
                  noOptionsMessage={deviceNoOptionsMessage}
                />
              </div>
              {!noFleetCompany && !hasRetransmissions && (
                <p className="text-[11px] text-amber-500 flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  {t("integradores.noRetransmissions")}
                </p>
              )}
            </div>
            {hasRetransmissions && (
              <div className="w-full sm:w-64">
                <Select<SelectOption>
                  value={retransmissionOption}
                  onChange={(opt) => {
                    setRetransmissionOption(opt);
                    if (opt) setDeviceOption(null);
                  }}
                  options={retransmissionScopeOptions}
                  placeholder={t("integradores.select.retransmission")}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                  isDisabled={noFleetCompany}
                />
              </div>
            )}
          </>
        )}

        <div
          className={`flex items-center rounded-lg p-0.5 ${isDarkMode ? "bg-dark-card" : "bg-gray-100"
            }`}
        >
          {(["all", "ok", "error"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-3 py-1 rounded-md transition-colors cursor-pointer ${statusFilter === s
                  ? s === "error"
                    ? "bg-red-500 text-white font-semibold"
                    : s === "ok"
                      ? "bg-emerald-500 text-white font-semibold"
                      : "bg-primary-500 text-dark-bg font-semibold"
                  : isDarkMode
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-700"
                }`}
            >
              {t(`integradores.status.${s}`)}
            </button>
          ))}
        </div>

        {!(mode === "internal" && scopeMode === "integrator") && (
          <div className="w-full sm:w-64">
            <Select<SelectOption>
              value={
                selectedAdapter
                  ? { value: selectedAdapter, label: selectedAdapter }
                  : null
              }
              onChange={(opt) => setSelectedAdapter(opt?.value ?? null)}
              options={adapterFilterOptions}
              placeholder={t("integradores.selectIntegratorPlaceholder")}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              isClearable
            />
          </div>
        )}

        {showRetransmissionFilter && (
          <div className="w-full sm:w-64">
            <Select<SelectOption>
              value={
                retransmissionFilterOptions.find(
                  (o) => o.value === selectedRetransmission
                ) ?? null
              }
              onChange={(opt) => setSelectedRetransmission(opt?.value ?? null)}
              options={retransmissionFilterOptions}
              placeholder={t("integradores.select.retransmission")}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              isClearable
            />
          </div>
        )}

        <div className="relative flex items-center flex-1 sm:flex-none min-w-0">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="absolute left-2.5 text-xs pointer-events-none text-gray-400"
          />
          <input
            placeholder={t("integradores.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`${inpCls} w-full sm:w-56 pl-8`}
          />
        </div>

        <button onClick={clearEvidences} className={btnGhost}>
          <FontAwesomeIcon icon={faTrash} />
          {t("integradores.clear")}
        </button>
      </div>

      <div
        className={`px-3 sm:px-4 py-2 flex flex-wrap items-center gap-2 shrink-0 border-b ${isDarkMode ? "bg-dark-surface border-dark-border" : "bg-gray-50 border-gray-200"
          }`}
      >
        <StatCard label={t("integradores.stats.totalLabel")} value={stats.total} isDarkMode={isDarkMode} />
        <StatCard label={t("integradores.status.ok")} value={stats.ok} tone="ok" isDarkMode={isDarkMode} />
        <StatCard label={t("integradores.status.error")} value={stats.error} tone="error" isDarkMode={isDarkMode} />
        <StatCard label={t("integradores.stats.latencyLabel")} value={`${stats.avgLatency}ms`} isDarkMode={isDarkMode} />
      </div>

      {noFleetCompany && (
        <div className="bg-amber-500 text-dark-bg px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 shrink-0">
          <FontAwesomeIcon icon={faTimes} />
          {t("integradores.selectCompanyFirst")}
        </div>
      )}

      <div className="flex-1 flex flex-col lg:grid lg:grid-cols-[1fr_480px] overflow-hidden">
        <div
          className={`flex flex-col overflow-hidden lg:border-r ${isDarkMode ? "border-dark-border" : "border-gray-200"
            }`}
        >
          <div className="flex-1 overflow-auto">
            {evidences.length === 0 ? (
              <div
                className={`p-4 text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
              >
                {t("integradores.empty")}
              </div>
            ) : (
              evidences.map((ev) => (
                <div
                  key={ev._localId}
                  onClick={() => setSelected(ev)}
                  className={`px-3 py-2 cursor-pointer border-b border-l-[3px] flex items-center gap-3 ${selected?._localId === ev._localId
                      ? isDarkMode
                        ? "bg-dark-card border-b-dark-border border-l-primary-500"
                        : "bg-primary-50 border-b-gray-100 border-l-primary-500"
                      : isDarkMode
                        ? "bg-dark-surface border-b-dark-border border-l-transparent hover:bg-dark-card"
                        : "bg-white border-b-gray-100 border-l-transparent hover:bg-gray-50"
                    }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${ev.integrator_ok ? "bg-emerald-500" : "bg-red-500"
                      }`}
                  />
                  <span
                    className={`text-[13px] font-semibold w-36 truncate ${isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                  >
                    {ev.integrator_adapter}
                  </span>
                  {ev.ws_name && (
                    <span
                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 truncate max-w-[150px] ${isDarkMode
                          ? "bg-primary-500/10 text-primary-400"
                          : "bg-primary-50 text-primary-600"
                        }`}
                      title={ev.ws_name}
                    >
                      {ev.ws_name}
                    </span>
                  )}
                  {ev.historical && (
                    <span
                      className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 shrink-0 flex items-center gap-1"
                      title={t("integradores.historical.badge")}
                    >
                      <i className="ri-history-line"></i>
                      {t("integradores.historical.tag")}
                    </span>
                  )}
                  <span
                    className={`text-xs font-mono px-1.5 py-0.5 rounded ${ev.http_status >= 200 && ev.http_status < 300
                        ? "bg-emerald-500/10 text-emerald-400"
                        : "bg-red-500/10 text-red-400"
                      }`}
                  >
                    {ev.http_status}
                  </span>
                  <span
                    className={`text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                  >
                    {ev.latency_ms}ms
                  </span>
                  <span
                    className={`text-[11px] flex-1 truncate ${isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    title={ev.error || ev.job_id}
                  >
                    {ev.error || ev.job_id}
                  </span>
                  <span
                    className={`text-[11px] shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                  >
                    {formatTime(ev.sent_at)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div
          className={`flex flex-col overflow-hidden min-w-0 ${isDarkMode ? "bg-dark-surface" : "bg-gray-50"
            }`}
        >
          {!selected ? (
            <div
              className={`p-4 text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
            >
              {t("integradores.selectEvidence")}
            </div>
          ) : (
            <div className="flex flex-col overflow-auto p-3 gap-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FontAwesomeIcon
                    icon={faPlug}
                    className={`text-sm ${isDarkMode ? "text-primary-400" : "text-primary-600"
                      }`}
                  />
                  <span
                    className={`text-lg font-extrabold tracking-tight truncate ${isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    {selected.integrator_adapter}
                  </span>
                  {selected.reauthed && (
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 shrink-0">
                      reauth
                    </span>
                  )}
                  {selected.dry_run && (
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 shrink-0">
                      dry-run
                    </span>
                  )}
                  {selected.historical && (
                    <span
                      className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 shrink-0 flex items-center gap-1"
                      title={t("integradores.historical.badge")}
                    >
                      <i className="ri-history-line"></i>
                      {t("integradores.historical.tag")}
                    </span>
                  )}
                  {scopeMode === "all" && selected.company_id && (
                    <span
                      className={`flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${isDarkMode
                          ? "bg-primary-500/15 text-primary-400"
                          : "bg-primary-50 text-primary-600"
                        }`}
                      title={`${t("integradores.meta.company")}: ${companyNameById(selected.company_id)}`}
                    >
                      <i className="ri-building-2-line"></i>
                      <span className="truncate max-w-[140px]">
                        {companyNameById(selected.company_id)}
                      </span>
                    </span>
                  )}
                </div>
                <span
                  className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${selected.integrator_ok
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-red-500/15 text-red-400"
                    }`}
                >
                  {selected.integrator_ok ? "✓" : "✗"} {selected.http_status} ·{" "}
                  {selected.latency_ms}ms
                </span>
              </div>

              {selected.historical && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
                  <i className="ri-history-line text-amber-400 text-sm mt-0.5 shrink-0"></i>
                  <div className="flex flex-col min-w-0">
                    <span className="text-[11px] font-semibold text-amber-400">
                      {t("integradores.historical.badge")}
                    </span>
                    <span
                      className={`text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                    >
                      {t("integradores.historical.description")}
                    </span>
                    <span
                      className={`text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                    >
                      {t("integradores.historical.lastPosition", {
                        time: formatTime(selected.sent_at),
                      })}
                    </span>
                  </div>
                </div>
              )}

              <div
                className={`grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg p-3 ${isDarkMode
                    ? "bg-dark-bg border border-dark-border"
                    : "bg-white border border-gray-200"
                  }`}
              >
                <Meta label={t("integradores.meta.imei")} value={selected.imei || "—"} isDarkMode={isDarkMode} />
                <Meta label={t("integradores.meta.job")} value={selected.job_id || "—"} isDarkMode={isDarkMode} />
                <Meta label={t("integradores.meta.device")} value={selected.device_id || "—"} isDarkMode={isDarkMode} />
                <Meta label={t("integradores.meta.company")} value={selected.company_id ? companyNameById(selected.company_id) : "—"} isDarkMode={isDarkMode} />
                <Meta label={t("integradores.meta.integratorStatus")} value={selected.integrator_ok ? t("integradores.integratorStatus.active") : t("integradores.integratorStatus.inactive")} isDarkMode={isDarkMode} />
                <Meta label={t("integradores.meta.wsName")} value={selected.ws_name || "—"} isDarkMode={isDarkMode} />
                <Meta label={t("integradores.meta.retransmission")} value={selected.retransmission_id ? retransmissionNameById.get(selected.retransmission_id) || selected.ws_name || selected.retransmission_id : "—"} isDarkMode={isDarkMode} />
                <Meta label={t("integradores.meta.sent")} value={formatTime(selected.sent_at)} isDarkMode={isDarkMode} />
                <Meta label={t("integradores.meta.resp")} value={formatTime(selected.response_at)} isDarkMode={isDarkMode} />
              </div>

              {selected.error && (
                <div className="text-[11px] text-red-400 break-words border border-red-500/30 bg-red-500/10 rounded p-2">
                  {selected.error}
                </div>
              )}

              {(["request", "response"] as const).map((field) => {
                const formatted = formatPayload(selected[field]);
                const { ext } = payloadFileMeta(selected[field]);
                return (
                  <div key={field} className="flex flex-col">
                    <div className="flex items-center justify-between mb-1.5 gap-1 flex-wrap">
                      <span
                        className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${isDarkMode ? "text-gray-200" : "text-gray-700"
                          }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                        {t(`integradores.${field}`)}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => copyText(formatted, field)}
                          className={`${btnGhost} ${copied === field ? "!text-primary-500" : ""
                            }`}
                        >
                          <FontAwesomeIcon
                            icon={copied === field ? faCheck : faCopy}
                          />
                          {copied === field
                            ? t("integradores.copied")
                            : t("integradores.copy")}
                        </button>
                        <button
                          onClick={() => handleDownloadText(field)}
                          className={btnGhost}
                        >
                          <FontAwesomeIcon icon={faFileArrowDown} />
                          {ext.toUpperCase()}
                        </button>
                        <button
                          onClick={() => handleScreenshot(field)}
                          disabled={busyShot === `${field}-png`}
                          className={btnGhost}
                        >
                          <FontAwesomeIcon icon={faImage} />
                          PNG
                        </button>
                      </div>
                    </div>
                    <pre
                      className={`overflow-auto border rounded p-2.5 text-[11px] font-mono whitespace-pre-wrap break-words leading-relaxed max-h-72 ${isDarkMode
                          ? "bg-dark-bg border-dark-border text-gray-300"
                          : "bg-white border-gray-200 text-gray-700"
                        }`}
                    >
                      {formatted}
                    </pre>
                  </div>
                );
              })}

              {joinedRooms.length > 0 && (
                <div
                  className={`text-[10px] ${isDarkMode ? "text-gray-600" : "text-gray-400"
                    }`}
                >
                  rooms: {joinedRooms.join(", ")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({
  label,
  value,
  isDarkMode,
}: {
  label: string;
  value: string;
  isDarkMode: boolean;
}) {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <span
        className={`text-[10px] font-bold uppercase tracking-wide shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
      >
        {label}
      </span>
      <span
        className={`text-[11px] font-mono truncate ${isDarkMode ? "text-gray-200" : "text-gray-800"
          }`}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
  isDarkMode,
}: {
  label: string;
  value: number | string;
  tone?: "ok" | "error";
  isDarkMode: boolean;
}) {
  const valueColor =
    tone === "ok"
      ? "text-emerald-400"
      : tone === "error"
        ? "text-red-400"
        : isDarkMode
          ? "text-gray-100"
          : "text-gray-900";

  return (
    <div
      className={`flex items-baseline gap-1.5 px-2.5 py-1 rounded-md ${isDarkMode ? "bg-dark-card" : "bg-white border border-gray-200"
        }`}
    >
      <span className={`text-sm font-bold ${valueColor}`}>{value}</span>
      <span
        className={`text-[10px] uppercase tracking-wide ${isDarkMode ? "text-gray-500" : "text-gray-400"
          }`}
      >
        {label}
      </span>
    </div>
  );
}
